import { Notifications, NotificationsNone } from '@mui/icons-material'
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
  useTheme,
} from '@mui/material'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useGetUnreadNotificationCount,
  useGetUnreadNotifications,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
} from 'src/api/notification'
import { AppRoutesNavigation } from 'src/app-routes'
import { INotification } from 'src/generated-types/notification'
import { getLocalFormattedDateTimeFromUTCString } from 'src/utils/function'

interface NotificationBellProps {
  customerId: EntityId
}

export const NotificationBell: FC<NotificationBellProps> = ({ customerId }) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { data: unreadCount } = useGetUnreadNotificationCount(customerId)
  const { data: notifications } = useGetUnreadNotifications(customerId)
  const { mutateAsync: markAllAsRead } = useMarkAllNotificationsAsRead(customerId)
  const { mutateAsync: markAsRead } = useMarkNotificationAsRead(customerId)

  const open = Boolean(anchorEl)
  const hasUnread = !!unreadCount && unreadCount > 0

  const handleMarkAllRead = async () => {
    await markAllAsRead()
  }

  const handleNotificationClick = async (notification: INotification) => {
    await markAsRead(notification.id)
    setAnchorEl(null)
    if (notification.link) navigate(notification.link)
  }

  // Prefer localized rendering via the message key; fall back to the stored text.
  const renderTitle = (n: INotification) => (n.messageKey ? t(`notif.${n.messageKey}.title`, { ...n.data }) : n.title)
  const renderBody = (n: INotification) => (n.messageKey ? t(`notif.${n.messageKey}.body`, { ...n.data }) : n.body)

  return (
    <>
      <IconButton
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{ width: 44, height: 44, color: theme.palette.primaryMain[theme.palette.mode] }}
      >
        <Badge badgeContent={unreadCount ?? 0} color="error">
          {hasUnread ? <Notifications sx={{ fontSize: 30 }} /> : <NotificationsNone sx={{ fontSize: 30 }} />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('notifications')}
          </Typography>
          {notifications && notifications.length > 0 && (
            <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }} onClick={handleMarkAllRead}>
              {t('mark-all-read')}
            </Typography>
          )}
        </Box>
        <Divider />

        {notifications && notifications.length > 0 ? (
          <List dense sx={{ p: 0 }}>
            {notifications.map(n => (
              <ListItem key={n.id} disablePadding>
                <ListItemButton onClick={() => handleNotificationClick(n)} sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={n.isRead ? 'normal' : 'bold'}>
                        {renderTitle(n)}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {(() => {
                            const text = renderBody(n)
                            return text.length > 90 ? `${text.substring(0, 90)}…` : text
                          })()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getLocalFormattedDateTimeFromUTCString(n.createdAt, 'YYYY-MM-DD HH:mm')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              {t('no-new-notifications')}
            </Typography>
          </Box>
        )}

        <Divider />
        <Box p={1.5}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('notifications-footer-why')}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: 'pointer', fontWeight: 600 }}
            onClick={() => {
              setAnchorEl(null)
              navigate(AppRoutesNavigation.Profile)
            }}
          >
            {t('notifications-footer-manage')}
          </Typography>
        </Box>
      </Popover>
    </>
  )
}

export default NotificationBell
